import {
    CohortIcon,
    ListItemButtonContent,
    StyledItemText,
    StyledListItem,
    StyledLink,
} from '@/components/Hypertracker/HypertrackerSidebar/HypertrackerSidebarStyled';
import { useTheme, Stack, Typography, Menu } from '@mui/material';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemButton from '@mui/material/ListItemButton';
import { useTranslation } from 'react-i18next';
import React, { memo, useMemo, useRef, useState } from 'react';
import { pnlSegments, SegmentId, SelectOption, sizeSegments } from '@/lib/types/hypertrackerTypes';
import { useNavigate, useParams } from 'react-router';
import { ROUTES } from '@/lib/constants';
import MenuItem from '@mui/material/MenuItem';
import {
    segmentIdToLabel,
    segmentLabelToId,
    segmentsCriteriaLabels,
    segmentsLabels,
} from '@/lib/constants/hyperTrackerConstants';

const CohortsMenu = ({ menuItem, open, isSelected, setOpen }) => {
    const theme = useTheme();
    const [isCohortsOpen, setIsCohortsOpen] = useState(false);
    const [anchorEl, setAnchorEl] = useState<any>(null);
    const { t } = useTranslation('translation');
    const params = useParams();

    const currentSegment = Number(params?.segment)
        ? params.segment
        : segmentLabelToId?.[params?.segment || ''];
    const navigate = useNavigate();
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const lastClickRef = useRef<number>(0);
    const debounceInterval = 500;

    const handleCohorts = (cohort) => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        setAnchorEl(null);
        setIsCohortsOpen(false);
        setOpen(false);
        navigate(`${ROUTES.HYPERTRACKER_SEGMENTS}/${segmentIdToLabel?.[+cohort.value]}`);
    };

    const handleLeave = () => {
        timeoutRef.current = setTimeout(() => {
            setIsCohortsOpen(false);
            setAnchorEl(null);
            setOpen(false);
        }, 200);
    };

    const handleEnter = (event: React.MouseEvent<HTMLElement>) => {
        const now = Date.now();
        const diff = now - lastClickRef.current;

        lastClickRef.current = now;
        if (isCohortsOpen || anchorEl || diff < debounceInterval) {
            handleLeave();
            return;
        }

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        setAnchorEl(event.currentTarget);
        setIsCohortsOpen(true);
    };

    const sizeSegmentsOptions: SelectOption<SegmentId>[] = useMemo(() => {
        return sizeSegments.map((segment) => ({
            label: t(segmentsLabels[segment]),
            value: segment,
        }));
    }, [t]);

    const pnlSegmentsOptions: SelectOption<SegmentId>[] = useMemo(() => {
        return pnlSegments.map((segment) => ({
            label: t(segmentsLabels[segment]),
            value: segment,
        }));
    }, [t]);

    return (
        <>
            <StyledListItem
                key={menuItem.label}
                disablePadding
                onClick={handleEnter}
                disableGutters
            >
                <ListItemButton
                    disableGutters
                    // onMouseEnter={handleEnter}
                    // onMouseLeave={handleLeave}
                    sx={{
                        minHeight: 32,
                        justifyContent: open ? 'initial' : 'center',
                        padding: '0 12px',
                        '&:hover': { backgroundColor: 'transparent' },
                    }}
                >
                    <ListItemButtonContent
                        sx={{
                            opacity: 1,
                            color: isSelected
                                ? theme.palette.common.white
                                : theme.palette.primitives.woodsmoke[500],
                            '&:hover': { color: theme.palette.common.white },
                            '& .MuiListItemIcon-root': { color: 'inherit' },
                        }}
                    >
                        <ListItemIcon
                            sx={{
                                minWidth: 0,
                            }}
                        >
                            <CohortIcon sx={{ path: { stroke: 'currentColor' } }} />
                        </ListItemIcon>
                        <StyledItemText
                            primary={
                                <Stack
                                    direction={'row'}
                                    alignItems="center"
                                    justifyContent="space-between"
                                >
                                    <Typography
                                        variant={'p14'}
                                        color="inherit"
                                        sx={{
                                            marginLeft: '12px',
                                            textTransform: 'capitalize',
                                        }}
                                    >
                                        {t(`hypertracker_sidebar.${menuItem.label}`)}
                                    </Typography>
                                    <ArrowForwardIosIcon
                                        sx={{
                                            width: '12px',
                                        }}
                                    />
                                </Stack>
                            }
                            sx={{ opacity: open ? 1 : 0 }}
                        />
                    </ListItemButtonContent>
                </ListItemButton>
                <Menu
                    open={isCohortsOpen}
                    onClose={handleLeave}
                    disableAutoFocusItem
                    anchorEl={anchorEl}
                    onClick={handleLeave}
                    MenuListProps={{
                        onMouseLeave: handleLeave,
                        // onMouseEnter: () => clearTimeout(timeoutRef.current!),
                    }}
                    sx={{
                        borderRadius: '6px',
                        boxShadow: '0px 1px 12px 0px rgba(25, 27, 35, 0.15)',
                        left: '240px',
                        top: '-30px',
                        '& .MuiList-root': {
                            display: 'grid',
                            gidTemplateColumns: '1fr 1fr',
                            background: theme.palette.backgrounds.htBgGradient,
                        },
                    }}
                >
                    <MenuItem
                        sx={{
                            gridRow: '1',
                            gridColumn: '1 / 2',
                            borderBottom: '1px solid rgba(255, 255, 255, 0.10)',
                            background: 'transparent !important',
                            opacity: '1 !important',
                        }}
                        value=""
                        disabled
                    >
                        <Typography variant={'p12'} color="common.white">
                            {t('header.size')}
                        </Typography>
                    </MenuItem>
                    {sizeSegmentsOptions.map((item, index) => {
                        return (
                            <StyledLink
                                key={item.value}
                                href={`${ROUTES.HYPERTRACKER_SEGMENTS}/${segmentIdToLabel?.[+item.value]}`}
                                onClick={(e) => {
                                    if (
                                        e.button === 0 &&
                                        !e.ctrlKey &&
                                        !e.metaKey &&
                                        !e.shiftKey &&
                                        !e.altKey
                                    ) {
                                        e.preventDefault();
                                        handleCohorts(item);
                                    }
                                }}
                            >
                                <MenuItem
                                    sx={{
                                        gridRow: index + 2,
                                        gridColumn: '1 / 2',
                                    }}
                                    value={item.value}
                                    selected={+(currentSegment || 0) === item.value}
                                >
                                    <Typography variant="p12" color="customText.text4">
                                        {item.label}
                                    </Typography>
                                    &nbsp;
                                    <Typography variant="p12" color="customText.text1">
                                        {t(segmentsCriteriaLabels[item.value as SegmentId])}
                                    </Typography>
                                </MenuItem>
                            </StyledLink>
                        );
                    })}

                    <MenuItem
                        sx={{
                            gridRow: '1',
                            gridColumn: '2 / 3',
                            borderBottom: '1px solid rgba(255, 255, 255, 0.10)',
                            borderTop: 'none',
                            background: 'transparent !important',
                            opacity: '1 !important',
                        }}
                        value=""
                        disabled
                    >
                        <Typography variant={'p12'} color="common.white">
                            {t('header.pnl')}
                        </Typography>
                    </MenuItem>
                    {pnlSegmentsOptions.map((item, index) => {
                        return (
                            <MenuItem
                                sx={{
                                    gridRow: index + 2,
                                    gridColumn: '2 / 3',
                                }}
                                key={item.value}
                                value={item.value}
                                onClick={() => handleCohorts(item)}
                                selected={+(currentSegment || 0) === item.value}
                            >
                                <Typography variant="p12" color="customText.text4">
                                    {item.label}
                                </Typography>
                                &nbsp;
                                <Typography variant="p12" color="customText.text1">
                                    {t(segmentsCriteriaLabels[item.value as SegmentId])}
                                </Typography>
                            </MenuItem>
                        );
                    })}
                </Menu>
            </StyledListItem>
        </>
    );
};

export default memo(CohortsMenu);
